'use client';
import React, { FC, useEffect, useState, useRef } from 'react';

import LeaderCard from '@/containers/OurLeadership/LeaderCard';
import SectionTitle from '@/containers/OurLeadership/SectionTitle';
import UnderlinedButton from '@/containers/OurLeadership/UnderlinedButton';
import { TypeOurPeopleWithoutUnresolvableLinksResponse } from '@/features/contentful/_generated';
import { useSettingsContext } from '@/contexts/SettingsContext';

type TGroupCompaniesSectionProps = {
    peoples: TypeOurPeopleWithoutUnresolvableLinksResponse[];
    currentDepartment: string;
    handleModalOpen: (entityId: string) => void;
    hidePreview?: boolean;
};

const useIsMobile = () => {
    const [isMobile, setIsMobile] = useState(false);

    useEffect(() => {
        if (typeof window !== 'undefined') {
            const mediaQuery = window.matchMedia('(max-width: 767px)');

            setIsMobile(mediaQuery.matches);
            const handleMediaQueryChange = (event: MediaQueryListEvent) => {
                setIsMobile(event.matches);
            };
            mediaQuery.addEventListener('change', handleMediaQueryChange);
            return () => {
                mediaQuery.removeEventListener('change', handleMediaQueryChange);
            };
        }
    }, []);

    return isMobile;
};

const GroupCompaniesSection: FC<TGroupCompaniesSectionProps> = ({
    peoples,
    currentDepartment,
    handleModalOpen,
    hidePreview = false,
}) => {
    const [showMore, setShowMore] = useState(false);
    const anchorRef = useRef<HTMLDivElement | null>(null);
    const isMobile = useIsMobile();
    const MAX_PEOPLE_BEFORE_COLLAPSE = isMobile ? 2 : 3;
    const peoplesToShow = showMore ? peoples : peoples.slice(0, MAX_PEOPLE_BEFORE_COLLAPSE);
    const { getLabel } = useSettingsContext();
    const SHOW_MORE_LABEL = getLabel('label', 'showMore');
    const SHOW_LESS_LABEL = getLabel('label', 'showLess');
    const HEADER_HEIGHT_OFFSET = 100;

    const handleShowMoreClick = (showMore: boolean) => {
        setShowMore(!showMore);

        if (showMore && anchorRef.current) {
            const yOffset = anchorRef.current.getBoundingClientRect().top + window.scrollY - HEADER_HEIGHT_OFFSET;
            window.scrollTo({ top: yOffset, behavior: "smooth" });
        } 
    }

    if (peoplesToShow.length === 0) {
        return null;
    }

    return (
        <section ref={anchorRef} className="tw-flex tw-flex-col tw-items-center tw-mb-2">
            <SectionTitle title={currentDepartment} />
            <div className="tw-grid tw-grid-cols-2 sm:tw-grid-cols-2 md:tw-grid-cols-3 lg:tw-grid-cols-3 xl:tw-grid-cols-3 tw-gap-3 lg:tw-gap-5">
                {peoplesToShow.map((people) => (
                    <LeaderCard
                        key={people.sys.id}
                        id={people.sys.id}
                        image={`https:${people.fields.picture?.fields.file?.url}`}
                        imageAlt={people.fields.picture?.fields.title}
                        name={people.fields.name}
                        position={people.fields.position}
                        onModalOpen={() => {
                            handleModalOpen(people.sys.id);
                        }}
                        hidePreview={hidePreview}
                    />
                ))}
            </div>
            {peoples.length > MAX_PEOPLE_BEFORE_COLLAPSE ? (
                <div className="tw-pb-[12px]">
                    <UnderlinedButton
                        text={showMore ? SHOW_LESS_LABEL : SHOW_MORE_LABEL}
                        onClick={() => handleShowMoreClick(showMore)}
                    />
                </div>
            ) : null}
        </section>
    );
};

export default GroupCompaniesSection;
